import store from 'js/lib/coursera.store';

import type { SGASubmitter } from 'bundles/grading/components/staff-graded-assignments/types/SGAGradingTypes';
import { hasSubmission, isGraded } from 'bundles/grading/utils/submissionUtils';
import { getSubmitterFromListById } from 'bundles/grading/utils/submitterUtils';

// interface of all ids relevant to initial entry into quick grader
export interface EntryPointIds {
  submitterId?: string;
  partId?: string;
  rubricId?: string;
  lastPart?: string;
}

// interface of a reentry log item for a specific assignment
interface ReentryLogItem extends EntryPointIds {
  timestamp: number;
}

export class ReentryLogManager {
  // default local storage key for reentry log
  private static DEFAULT_REENTRY_LOG_KEY = 'quick-grader-reentry-log';

  // default milliseconds till reentry log item is stale
  private static DEFAULT_STALE_REENTRY_LOG_ITEM_MS = 2629800000; // 1-month in milliseconds

  private declare logKey: string;

  constructor(contextId: string, itemId: string, gradingGroupId?: string) {
    this.logKey = `${contextId}~${itemId}${gradingGroupId ? `~${gradingGroupId}` : ''}`;
  }

  /**
   * Removes stale reentry log items from the local storage
   */
  static removeStaleReentryLogItems = () => {
    const reentryLog: { [key: string]: ReentryLogItem } = store.get(ReentryLogManager.DEFAULT_REENTRY_LOG_KEY);
    if (reentryLog) {
      const currentTimestamp = Date.now();
      for (const key in reentryLog) {
        // ensure this key exists in the log, and check if it's stale; if so delete the log item
        if (
          reentryLog?.[key]?.timestamp &&
          currentTimestamp - reentryLog[key].timestamp > ReentryLogManager.DEFAULT_STALE_REENTRY_LOG_ITEM_MS
        ) {
          delete reentryLog[key];
        }
      }

      // reset updated reentry log to local storage
      store.set(ReentryLogManager.DEFAULT_REENTRY_LOG_KEY, reentryLog);
    }
  };

  /**
   * Gets the appropriate first submitter's submitterId that should be
   * presented in the left panel when using the entry point button
   *
   * Will first check local storage to see if there's a previous session.
   * Otherwise select first ungraded submitter; if all graded then first
   * graded; if no submissions then first no-submission submitter
   *
   * Does not work for courses with 0 submitters as the BE will give
   * an error that's handled on component side
   *
   * @param { SGASubmitter[] } submittedGroup
   * @param { SGASubmitter[] } notSubmittedGroup
   * @returns EntryPointIds
   */
  getFirstSubmitterIdForGradingEntry = (
    submittedGroup: SGASubmitter[],
    notSubmittedGroup: SGASubmitter[]
  ): EntryPointIds => {
    // grab the previousSessionIds that may exist in local storage; if it exists return that
    // check whether it exists in the submitters group to ensure that an old submitter within the stale period that may no longer exist in assignment doesn't get through
    // finally we want to make sure the submitter in local storage is not graded; if they're graded we want to find any un-graded submitters instead down below
    const previousSessionIds = this.getPreviousSessionFromLocalStorage();
    const existingSubmitter =
      previousSessionIds && previousSessionIds.submitterId
        ? getSubmitterFromListById(previousSessionIds.submitterId, submittedGroup.concat(notSubmittedGroup))
        : null;
    if (
      previousSessionIds &&
      previousSessionIds.submitterId &&
      existingSubmitter &&
      hasSubmission(existingSubmitter.submittedAt) &&
      !existingSubmitter.isSubmissionGraded
    ) {
      return previousSessionIds;
    }

    // when there's no existing submitter in the reentry log, we want the grader to be presented with the first un-graded submitter
    // if every submission is graded, then use either the existing one in the reentry log, or the first submission available
    // otherwise we should present the grader with the first un-submitted submitter if available
    let firstSubmitterId;
    if (submittedGroup.length > 0) {
      firstSubmitterId =
        submittedGroup.find(
          (submitterWithSubmission: SGASubmitter) => !isGraded(submitterWithSubmission.submissionStatus)
        )?.id ||
        previousSessionIds?.submitterId ||
        submittedGroup[0].id;
    } else {
      firstSubmitterId = previousSessionIds?.submitterId || notSubmittedGroup?.[0]?.id;
    }

    // save this to current session to local storage and return
    this.saveCurrentSessionToLocalStorage({ submitterId: firstSubmitterId });
    return { submitterId: firstSubmitterId };
  };

  /**
   * Removes specific log item from reentry log
   *
   * @param { string } contextId
   * @param { string } itemId
   * @param { string } gradingGroupId
   */
  removeReentryLogItem = () => {
    const reentryLog: { [key: string]: ReentryLogItem } = store.get(ReentryLogManager.DEFAULT_REENTRY_LOG_KEY);
    if (reentryLog && reentryLog?.[this.logKey]) {
      delete reentryLog[this.logKey];
      store.set(ReentryLogManager.DEFAULT_REENTRY_LOG_KEY, reentryLog);
    }
  };

  /**
   * Saves given reentryLogItem to local storage to remember current session
   *
   * @param { ReentryLogItemParams } params
   */
  saveCurrentSessionToLocalStorage = ({ submitterId, partId, rubricId, lastPart }: EntryPointIds) => {
    const logItemToSave = {
      [this.logKey]: {
        timestamp: Math.ceil(Date.now() / 10000) * 10000, // round to the nearest 10-seconds to avoid unnecessary extra reassignments to local storage
        submitterId,
        partId,
        rubricId,
        lastPart,
      } as ReentryLogItem,
    };

    // grab reentry log from local storage; if it exists, update the log and set back into local storage,
    // otherwise set new logItem to local storage
    const reentryLog: { [key: string]: ReentryLogItem } = store.get(ReentryLogManager.DEFAULT_REENTRY_LOG_KEY);
    if (reentryLog) {
      const result = Object.assign(reentryLog, logItemToSave);
      store.set(ReentryLogManager.DEFAULT_REENTRY_LOG_KEY, result);
    } else {
      store.set(ReentryLogManager.DEFAULT_REENTRY_LOG_KEY, logItemToSave);
    }
  };

  /**
   * Gets route ids associated to previous session if it exists in
   * local storage. Returns null if not
   *
   * @param { ReentryLogItemParams } params
   * @returns EntryPointIds | null
   */
  getPreviousSessionFromLocalStorage = () => {
    const reentryLog: { [key: string]: ReentryLogItem } = store.get(ReentryLogManager.DEFAULT_REENTRY_LOG_KEY);

    if (reentryLog && reentryLog?.[this.logKey]) {
      return {
        submitterId: reentryLog[this.logKey].submitterId,
        partId: reentryLog[this.logKey].partId,
        rubricId: reentryLog[this.logKey].rubricId,
        lastPart: reentryLog[this.logKey].lastPart,
      } as EntryPointIds;
    } else {
      return null;
    }
  };
}
